import React from 'react';
import { Dismiss20Regular } from '@fluentui/react-icons';
import { useDispatch, useSelector } from 'react-redux';
import { PostWorkstream, PatchWorkstream, GetWorkstreamsDefaults } from '../../../redux/dataroomSlice';
import { AiOutlineLoading3Quarters } from 'react-icons/ai';
import styles from '../css/vdr.module.css';

/**
 * Create / Edit workstream — Figma 6971:22779.
 * Description is saved with the workstream.
 * Defaults catalog remains available via "Add from catalog".
 */
const AddWorkstreamModal = ({ open, onClose, editWorkstream }) => {
    const dispatch = useDispatch();
    const { selectedProject } = useSelector(state => state?.projectData);
    const { default_workstreams, workstreams, addWsLoading } = useSelector(state => state?.dataroom);
    const [name, setName] = React.useState('');
    const [description, setDescription] = React.useState('');
    const [showCatalog, setShowCatalog] = React.useState(false);

    React.useEffect(() => {
        if (open) {
            dispatch(GetWorkstreamsDefaults());
            setName(editWorkstream?.name || '');
            setDescription(editWorkstream?.description || '');
            setShowCatalog(false);
        }
    }, [open, editWorkstream, dispatch]);

    if (!open) return null;

    const handleCreate = async () => {
        if (!name.trim()) return;
        const descriptionText = description.trim();
        if (editWorkstream) {
            await dispatch(
                PatchWorkstream({
                    name: name.trim(),
                    description: descriptionText,
                    id: editWorkstream.id,
                    project: localStorage['project_id'],
                }),
            );
        } else {
            await dispatch(
                PostWorkstream({
                    name: name.trim(),
                    description: descriptionText,
                    project: selectedProject?.id,
                    vdr: selectedProject?.vdr?.id,
                }),
            );
        }
        onClose();
    };

    const handleAddDefault = async wsName => {
        await dispatch(PostWorkstream({ name: wsName, project: selectedProject?.id, vdr: selectedProject?.vdr?.id }));
        onClose();
    };

    const title = editWorkstream ? 'Edit workstream' : 'Create workstream';

    return (
        <div className={styles.overlay} role="dialog" aria-modal="true">
            <div className={styles.modal}>
                <div className={styles.modalHeader}>
                    <h2 className={styles.modalTitle}>{title}</h2>
                    <button type="button" className={styles.iconBtn} onClick={onClose} aria-label="Close">
                        <Dismiss20Regular />
                    </button>
                </div>
                <div className={styles.modalBody}>
                    {!editWorkstream && (
                        <div style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: 8 }}>
                            <button type="button" className={styles.textLink} onClick={() => setShowCatalog(v => !v)}>
                                {showCatalog ? 'Create custom' : 'Add from catalog'}
                            </button>
                        </div>
                    )}

                    {showCatalog && !editWorkstream ? (
                        <>
                            {(default_workstreams || [])
                                .filter(ws => !(workstreams || []).some(w => w.name === ws.workstream))
                                .map(ws => (
                                    <div key={ws.id} className={styles.relatedRow} onClick={() => handleAddDefault(ws.workstream)}>
                                        <span>{ws.workstream}</span>
                                        <span className={styles.relatedCount}>Add →</span>
                                    </div>
                                ))}
                            {(default_workstreams || []).filter(ws => !(workstreams || []).some(w => w.name === ws.workstream)).length === 0 && (
                                <p className={styles.helperText}>All default workstreams are already added.</p>
                            )}
                        </>
                    ) : (
                        <>
                            <label className={styles.fieldLabel}>
                                Workstream name <span className={styles.requiredMark}>*</span>
                            </label>
                            <input
                                className={styles.input}
                                value={name}
                                onChange={e => setName(e.target.value)}
                                placeholder="e.g. ESG & Sustainability"
                            />
                            <label className={styles.fieldLabel}>Description</label>
                            <textarea
                                className={styles.textarea}
                                value={description}
                                onChange={e => setDescription(e.target.value)}
                            />
                        </>
                    )}
                </div>
                {(!showCatalog || editWorkstream) && (
                    <div className={styles.modalFooter}>
                        <button type="button" className={styles.primaryBtn} onClick={handleCreate} disabled={addWsLoading || !name.trim()}>
                            {editWorkstream ? 'Save changes' : 'Create workstream'}{' '}
                            {addWsLoading && <AiOutlineLoading3Quarters className="rotateLoader" />}
                        </button>
                        <button type="button" className={styles.secondaryBtn} onClick={onClose}>
                            Cancel
                        </button>
                    </div>
                )}
            </div>
        </div>
    );
};

export default AddWorkstreamModal;
